import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import AppleSignInButton from "../components/AppleSignInButton";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { api, setUser, setToken, API } from "../lib/api";
import { capture, identifyUser } from "../lib/analytics";
import { BUILD_ID } from "../build-info";
import { User as UserIcon, Truck, ArrowRight, Eye, EyeOff, Recycle, ChevronLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Two-sided marketplace. Admin role intentionally absent — admin is a locked
// owner account that cannot be self-registered. See backend/deps.py ADMIN_EMAIL.
//
// Behind the scenes "customer" and "reuse" both map to the existing backend
// role `homeowner` (they share identical account capabilities); "reuse" is a
// UX-only distinction that steers the user to the Reuse marketplace after
// sign-up instead of the customer dashboard.
const ROLES = [
  {
    id: "customer",
    label: "I need a collection",
    sub: "Post a job and get quotes from licensed carriers.",
    icon: Trash2,
  },
  {
    id: "carrier",
    label: "I'm a waste carrier",
    sub: "Find local jobs, bid, and get paid.",
    icon: Truck,
  },
  {
    id: "reuse",
    label: "I want to give or find free items",
    sub: "List items for free or browse what's available.",
    icon: Recycle,
  },
];

const toBackendRole = (uiRole) => (uiRole === "carrier" ? "carrier" : "homeowner");

const postSignupRoute = (uiRole) => {
  if (uiRole === "reuse") return "/reuse";
  if (uiRole === "customer") return "/post-waste";
  return "/dashboard";
};

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("customer");
  // Task 1 — new registration flow: after tapping "Create account" the user
  // must first pick one of three role cards. Only then does the actual form
  // appear, pre-filled with that role. `roleChosen === null` means we're on
  // the picker step; setting a value moves us to the form step. Falls back
  // to `null` whenever the user switches into register mode.
  const [roleChosen, setRoleChosen] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [license, setLicense] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedCarrierTerms, setAcceptedCarrierTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  // Submit handler — single function for both Sign-in and Create-account.
  // Performs client-side validation first, then calls the matching endpoint.
  // On success, persists the JWT + user object and routes to dashboard/admin.
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter your email and password.");
      return;
    }
    if (mode === "register") {
      if (!name.trim()) { toast.error("Please enter your name."); return; }
      if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
      if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
        toast.error("Password must contain at least one letter and one number."); return;
      }
      if (password !== passwordConfirm) { toast.error("Passwords do not match."); return; }
      if (!acceptedTerms) { toast.error("Please accept the Terms of Service and Privacy Policy."); return; }
      if (role === "carrier" && !acceptedCarrierTerms) {
        toast.error("Please confirm you understand carriers are independent businesses, not Binshare employees.");
        return;
      }
      if (role === "carrier") {
        if (!company.trim()) { toast.error("Please enter your company or trading name."); return; }
        if (!phone.trim()) { toast.error("Please enter a contact phone number."); return; }
      }
    }

    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email, password });
        setToken(data.token);
        setUser(data.user);
        identifyUser(data.user);
        capture("signin_completed", { method: "email", role: data.user.role });
        toast.success(`Welcome back, ${data.user.name.split(" ")[0]}!`);
        navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
      } else {
        const payload = {
          name,
          email,
          role: toBackendRole(role),
          company: company || null,
          license_no: license || null,
          phone: phone || null,
          password,
          password_confirm: passwordConfirm,
          accepted_terms: acceptedTerms,
          accepted_carrier_terms: role === "carrier" ? acceptedCarrierTerms : null,
        };
        const { data } = await api.post("/auth/register", payload);
        setToken(data.token);
        setUser(data.user);
        identifyUser(data.user);
        capture("signup_completed", { method: "email", role: data.user.role, chosen: role });
        toast.success(`Account created — welcome, ${data.user.name.split(" ")[0]}!`);
        navigate(postSignupRoute(role));
      }
    } catch (err) {
      // Distinguish 3 failure modes so users (and support screenshots) can tell
      // credential errors from network / CORS / server errors apart. Log the
      // raw error to the console every time — invaluable when a user pastes
      // a DevTools screenshot to support.
      // eslint-disable-next-line no-console
      console.error("[auth]", { status: err?.response?.status, data: err?.response?.data, message: err?.message });
      const detail = err?.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (!err?.response) {
        // No HTTP response → network failure, CORS, or server unreachable.
        // Surface the exact URL + build so a screenshot fully diagnoses
        // wrong-backend-baked-in situations without needing devtools.
        const host = (() => {
          try { return new URL(API).host; } catch { return API; }
        })();
        toast.error(
          `Can't reach ${host}. Check your connection. (build ${BUILD_ID})`,
          { duration: 8000 }
        );
      } else {
        toast.error(`Something went wrong (HTTP ${err.response.status}). Try Forgot password?`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    // hideChrome: removes the app bar + footer for a clean, focused auth screen
    // (per Android QA report). The brand logo at the top of the card replaces
    // the previous header link.
    <Layout hideChrome>
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-5 py-8 sm:py-12" style={{ paddingTop: "max(env(safe-area-inset-top), 2rem)" }}>
        <Card className="w-full max-w-md p-6 sm:p-8 rounded-2xl border-[#E2DFD8] bg-white shadow-sm">
          {/* Brand header */}
          <Link to="/" data-testid="auth-brand-link" className="flex items-center justify-center gap-2 mb-6">
            <img src="/icon-192.png?v=6" alt="Binshare" className="w-10 h-10 rounded-lg" />
            <span className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>Binshare</span>
          </Link>

          <h1 className="text-2xl sm:text-3xl text-center font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>
            {mode === "login" ? "Sign in" : roleChosen ? "Create your account" : "How will you use Binshare?"}
          </h1>
          <p className="text-sm text-center text-[#6B6965] mt-1">
            {mode === "login" ? "Welcome back to Binshare." : roleChosen ? "Join the UK's licensed waste marketplace." : "Choose the option that fits you best."}
          </p>

          {/* Login / Register tab toggle */}
          <div className="flex gap-2 mt-6 p-1 bg-[#EBE8E0] rounded-full">
            <button
              type="button"
              data-testid="tab-login"
              onClick={() => { setMode("login"); setRoleChosen(null); }}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${mode === "login" ? "bg-white shadow-sm" : "text-[#6B6965]"}`}
            >
              Sign in
            </button>
            <button
              type="button"
              data-testid="tab-register"
              onClick={() => { setMode("register"); setRoleChosen(null); }}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${mode === "register" ? "bg-white shadow-sm" : "text-[#6B6965]"}`}
            >
              Create account
            </button>
          </div>

          {/* Task 1 — role picker: shown as step 1 of registration */}
          {mode === "register" && !roleChosen && (
            <div data-testid="role-picker" className="mt-6 space-y-3">
              <p className="text-sm text-[#4A4842]">Pick how you want to use Binshare — you can only pick one.</p>
              {ROLES.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  data-testid={`role-card-${r.id}`}
                  onClick={() => { setRole(r.id); setRoleChosen(r.id); }}
                  className="w-full text-left flex items-start gap-4 p-4 rounded-2xl border-2 border-[#E2DFD8] bg-white hover:border-[#1B3624] hover:bg-[#F6F4F0] transition-all min-h-[88px]"
                >
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-[#1B3624] text-white flex items-center justify-center">
                    <r.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="text-base font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>{r.label}</div>
                    <div className="text-xs text-[#6B6965] leading-relaxed mt-1">{r.sub}</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#6B6965] mt-2 shrink-0" />
                </button>
              ))}
              <p className="text-xs text-center text-[#6B6965] pt-2">
                Already have an account?{" "}
                <button type="button" onClick={() => setMode("login")} data-testid="role-picker-signin" className="text-[#4A6B56] underline hover:text-[#1B3624]">
                  Sign in
                </button>
              </p>
            </div>
          )}

          {(mode === "login" || (mode === "register" && roleChosen)) && (
          <form onSubmit={handleSubmit} className="space-y-4 mt-6">
            {mode === "register" && (
              <>
                <div>
                  <Label htmlFor="input-name" className="text-sm">Full name</Label>
                  <Input
                    id="input-name"
                    data-testid="input-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Alex Patel"
                    className="rounded-xl mt-1"
                  />
                </div>
                <div data-testid="chosen-role-strip" className="flex items-center gap-3 rounded-xl border border-[#E2DFD8] bg-[#F6F4F0] px-3 py-2.5">
                  <div className="w-9 h-9 rounded-lg bg-[#1B3624] text-white flex items-center justify-center shrink-0">
                    {(() => { const RIcon = ROLES.find((r) => r.id === role)?.icon || UserIcon; return <RIcon className="w-4 h-4" />; })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-[#6B6965]">Signing up as</div>
                    <div className="text-sm font-medium text-[#1B3624] truncate" style={{ fontFamily: "Outfit" }}>
                      {ROLES.find((r) => r.id === role)?.label}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="change-role-btn"
                    onClick={() => setRoleChosen(null)}
                    className="inline-flex items-center gap-1 text-xs text-[#4A6B56] hover:text-[#1B3624]"
                  >
                    <ChevronLeft className="w-3 h-3" /> Change
                  </button>
                </div>
                {(role === "customer" || role === "reuse") && (
                  <div>
                    <Label htmlFor="input-company" className="text-sm">Company name <span className="text-[#6B6965] font-normal">(optional)</span></Label>
                    <Input
                      id="input-company"
                      data-testid="input-company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="Greenleaf Cafe Ltd"
                      className="rounded-xl mt-1"
                    />
                  </div>
                )}
                {role === "carrier" && (
                  <>
                    <div>
                      <Label htmlFor="input-carrier-company" className="text-sm">Company or trading name</Label>
                      <Input
                        id="input-carrier-company"
                        data-testid="input-carrier-company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        required
                        placeholder="e.g. Greenwheels Waste Ltd"
                        className="rounded-xl mt-1"
                      />
                      <p className="text-[11px] text-[#6B6965] mt-1">Shown to customers on your bids.</p>
                    </div>
                    <div>
                      <Label htmlFor="input-carrier-phone" className="text-sm">Contact phone number</Label>
                      <Input
                        id="input-carrier-phone"
                        data-testid="input-carrier-phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        placeholder="e.g. 07123 456789"
                        className="rounded-xl mt-1"
                      />
                      <p className="text-[11px] text-[#6B6965] mt-1">Only shared with customers you&apos;re working with, and Binshare ops.</p>
                    </div>
                    <div>
                      <Label htmlFor="input-license" className="text-sm">Waste-carrier licence no.</Label>
                      <Input
                        id="input-license"
                        data-testid="input-license"
                        value={license}
                        onChange={(e) => setLicense(e.target.value)}
                        placeholder="CBDU123456"
                        className="rounded-xl mt-1"
                      />
                      <p className="text-[11px] text-[#6B6965] mt-1">You&apos;ll upload the certificate after sign-up.</p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#F6F4F0] border border-[#E2DFD8]">
                      <p className="text-[11px] text-[#4A4842] leading-relaxed">
                        <strong>Binshare is a lead-generation marketplace, not an employer.</strong> You operate your own business, set your own prices, and are responsible for your own taxes, insurance and waste-disposal compliance.
                      </p>
                      <label className="mt-2 flex items-start gap-2 cursor-pointer text-xs text-[#1B3624]">
                        <input
                          data-testid="carrier-terms-checkbox"
                          type="checkbox"
                          checked={acceptedCarrierTerms}
                          onChange={(e) => setAcceptedCarrierTerms(e.target.checked)}
                          className="mt-0.5 w-4 h-4 accent-[#1B3624]"
                        />
                        <span>I confirm I&apos;m an <strong>independent business or self-employed contractor</strong>, not a Binshare employee.</span>
                      </label>
                    </div>
                  </>
                )}
              </>
            )}

            <div>
              <Label htmlFor="input-email" className="text-sm">Email</Label>
              <Input
                id="input-email"
                data-testid="input-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                autoComplete="email"
                className="rounded-xl mt-1"
              />
            </div>

            <div>
              <Label htmlFor="input-password" className="text-sm">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="input-password"
                  data-testid="input-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder={mode === "register" ? "At least 8 chars, 1 letter + 1 number" : "Your password"}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  className="rounded-xl pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6965] hover:text-[#1B3624]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === "login" && (
                <div className="mt-1.5 text-right">
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-xs text-[#4A6B56] hover:text-[#1B3624] underline"
                  >
                    Forgot your password?
                  </Link>
                </div>
              )}
            </div>

            {mode === "register" && (
              <div>
                <Label htmlFor="input-password-confirm" className="text-sm">Confirm password</Label>
                <Input
                  id="input-password-confirm"
                  data-testid="input-password-confirm"
                  type={showPassword ? "text" : "password"}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  className="rounded-xl mt-1"
                />
              </div>
            )}

            {mode === "register" && (
              <label className="flex items-start gap-2 cursor-pointer text-sm text-[#1B3624]">
                <input
                  data-testid="accept-terms-checkbox"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  required
                  className="mt-0.5 w-4 h-4 accent-[#1B3624]"
                />
                <span className="text-xs text-[#4A4842] leading-relaxed">
                  I agree to the{" "}
                  <Link to="/terms" data-testid="terms-link" className="text-[#4A6B56] underline hover:text-[#1B3624]">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/policies" data-testid="privacy-link" className="text-[#4A6B56] underline hover:text-[#1B3624]">Privacy Policy</Link>.
                </span>
              </label>
            )}

            <Button
              data-testid="submit-auth"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white py-6 mt-2"
            >
              {loading ? "Please wait…" : (mode === "login" ? "Sign in" : "Create account")}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </form>
          )}

          {/* Social sign-in — App Store guideline 4.8 requires equal placement */}
          {(mode === "login" || (mode === "register" && roleChosen)) && (
          <div className="mt-5">
            <div className="relative my-4 flex items-center">
              <div className="flex-1 h-px bg-[#E2DFD8]" />
              <span className="px-3 text-[11px] uppercase tracking-wider text-[#9B9893]">or</span>
              <div className="flex-1 h-px bg-[#E2DFD8]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <AppleSignInButton />
              <GoogleSignInButton />
            </div>
            {mode === "register" && (
              <p className="mt-2 text-[11px] text-[#6B6965] text-center">
                Social sign-in creates a customer account. Carriers must register with email to add their licence.
              </p>
            )}
          </div>
          )}

          {mode === "login" && (
            <p className="mt-5 text-center text-xs text-[#6B6965]">
              New to Binshare?{" "}
              <button type="button" data-testid="switch-to-register" onClick={() => setMode("register")} className="text-[#4A6B56] underline hover:text-[#1B3624]">
                Create an account
              </button>
            </p>
          )}
        </Card>
      </div>
    </Layout>
  );
}
